import { EventPanelMetadata } from '@mark43/cad-api';
import { autorun } from 'mobx';
import React, { useEffect, useState } from 'react';
import type { EventPanelMetadataStore } from '../event-state/event-panel-metadata-store';

const EventMetadataContext = React.createContext<undefined | EventPanelMetadataStore>(undefined);

export function EventMetadataProvider(props: {
    children: React.ReactElement;
    value: EventPanelMetadataStore;
}): React.ReactElement {
    return (
        <EventMetadataContext.Provider value={props.value}>
            {props.children}
        </EventMetadataContext.Provider>
    );
}

/**
 * This hook is specifically for use in other hooks as it requires specifying keys which should be tracked
 */
export function useEventMetadataWithTrackedKeys(
    keys: (keyof EventPanelMetadata)[] | readonly (keyof EventPanelMetadata)[]
): undefined | EventPanelMetadata {
    const store = React.useContext(EventMetadataContext);
    const [, setLocalState] = useState(true);

    if (!store) {
        throw new Error('Unexpectedly did not find metadata store');
    }

    useEffect(() => {
        return autorun(() => {
            for (const key of keys) {
                // We are accessing keys so they are properly deferenced and our side effect will be re-executed
                // when those values change.
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                store.state.metadata?.[key];
            }
            setLocalState((value) => !value);
        });
    }, [store, keys]);

    return store.state.metadata;
}

export function useEventMetadataStore(): EventPanelMetadataStore {
    const store = React.useContext(EventMetadataContext);
    if (!store) {
        throw new Error('Unexpectedly did not find metadata object');
    }
    return store;
}
